/**
 * @component
 * @name ApprovedMethodologyDetails
 *
 * @returns {JSX.Element} React component for displaying approved methodology details.
 *
 * @description Overview:
 * The `ApprovedMethodologyDetails` component is responsible for rendering the details of an approved methodology.
 * It fetches details of the methodology by its ID and displays information including the methodology header,
 * decisions, expert reviews, and comments related to the methodology.
 *
 * @see {@link MethodologyHeader} - Component responsible for rendering the header of the methodology.
 * @see {@link Decisions} - Component displaying decisions related to the methodology.
 * @see {@link Comments} - Component for rendering comments on the methodology.
 * @see {@link SEO} - Imported component for managing SEO metadata.
 * @see {@link useBasicMethodologyRoundDetails} - Hook used to retrieve basic details of the methodology rounds.
 *
 *
 */
import dynamic from "next/dynamic";
import { useEffect, useMemo, useState } from "react";

import userAtom from "@/atoms/userAtom";
import PublicComment from "@/components/methodology/approved/PublicComment";
import CondionallyApprovedMethodologyHeader from "@/components/methodology/CondionallyApprovedMethodologyHeader";
import ConditionalApprovalExpertReview from "@/components/methodology/reviewMethodology/ConditionalApprovalExpertReview";
import SEO from "@/components/shared/SEO";
import Loader from "@/components/shared/ui/Loader";
import useMethodologyRoundDetails from "@/hooks/methodologyReview/useMethodologyRoundDetails";
import { MethodologyAdditionalInfoResponse } from "@/types/AdditionalInfo";
import { isOriginator } from "@/utils/isOriginator";
import { useAtom } from "jotai";
import Decisions from "./approved/Decisions";
import Quantification from "./approved/Quantification";
import Summary from "./approved/Summary";

const CustomTab = dynamic(() => import("@/components/shared/ui/Tabs"), { ssr: false });

interface ConditionalApprovalPageProps {
	dataWithAdditionalInfo: MethodologyAdditionalInfoResponse | undefined;
	refetch: () => {};
	published?: true;
	isForMethodologySelection?: boolean;
	isDisplayingForSubmittedProject?: boolean;
}

const ApprovedMethodologyDetails = (props: ConditionalApprovalPageProps) => {
	const { dataWithAdditionalInfo, refetch } = props;
	const {
		roundDetails: data,
		isLoadingRoundDetails: isLoading,
		role,
		router,
		refetch: refetchRound,
		status,
	} = useMethodologyRoundDetails();
	const [publishDisabled] = useState<boolean>(true);
	const [user] = useAtom(userAtom);

	if (status === "error") {
		router.push("/404");
	}

	const isPDAndOriginator = data && user ? isOriginator(user, data.originatorId) : false;

	const sortedRoundIds = useMemo(
		() =>
			data?.rounds?.sort((a, b) => a.roundNumber - b.roundNumber).map((round) => round.methodologyRoundId.toString()),
		[data],
	);

	useEffect(() => {
		if (!data || !dataWithAdditionalInfo) return;

		if (data.status !== "APPROVED") {
			router.push(`/all-methodologies`);
		}
	}, [data, dataWithAdditionalInfo, user, router]);

	if (isLoading) return <Loader />;

	return (
		<>
			<SEO title="Methodology Summary" />

			{!!data && (
				<CondionallyApprovedMethodologyHeader
					methodology={data}
					publishDisabled={publishDisabled}
					additionalInfoData={dataWithAdditionalInfo?.methodologyAdditionalInfo}
					additionalInfoId={dataWithAdditionalInfo?.methodologyAdditionalInfo?.id}
					isOriginator={isPDAndOriginator}
					roundStatus={data?.methodologyRoundStatus}
					refetchRound={refetchRound}
					isForMethodologySelection={props.isForMethodologySelection}
					isDisplayingForSubmittedProject={props.isDisplayingForSubmittedProject}
				/>
			)}

			<aside className="mt-12">
				<CustomTab
					className="max-w[100%] inline-flex w-auto gap-2"
					tabHeaderOverride="w-auto"
					labels={["Summary", "Decisions", "Expert Review", "Public comments", "Quantification"]}
				>
					<Summary
						data={dataWithAdditionalInfo}
						onSubmitSuccessFn={refetch}
						isOriginator={isPDAndOriginator}
						roundStatus={data?.methodologyRoundStatus}
					/>
					<Decisions methodologyStatus={data?.status} />
					<ConditionalApprovalExpertReview roundDetails={data} sortedRoundIds={sortedRoundIds} role={role} />
					<PublicComment
						onSubmitSuccessFn={refetch}
						isOriginator={isPDAndOriginator}
						roundStatus={data?.methodologyRoundStatus}
					/>
					<Quantification
						onSubmitSuccessFn={refetch}
						isOriginator={isPDAndOriginator}
						roundStatus={data?.methodologyRoundStatus}
					/>
				</CustomTab>
			</aside>
		</>
	);
};

export default ApprovedMethodologyDetails;
